// Apgar Score — 2D matrix layout: 5 criteria × 3 time intervals (min 1/5/10).
// Each intersecting cell has three stacked segmented buttons (0 / 1 / 2)
// with short descriptions. Per-column totals shown at the bottom + a
// combined "T1 / T5 / T10" reporting string colour-coded by band.

import React, { useState, useMemo } from "react";
import { Baby, Copy, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const CRITERIA = [
  {
    key: "appearance",
    en: "Appearance",
    id: "Appearance",
    options: [
      { score: 0, en: "Blue / pale all over", id: "Biru / pucat" },
      { score: 1, en: "Body pink, extremities blue", id: "Tubuh pink, ekstremitas biru" },
      { score: 2, en: "Completely pink", id: "Merah muda seluruh tubuh" },
    ],
  },
  {
    key: "pulse",
    en: "Pulse",
    id: "Pulse",
    options: [
      { score: 0, en: "Absent", id: "Tidak ada" },
      { score: 1, en: "< 100 bpm", id: "< 100 bpm" },
      { score: 2, en: "≥ 100 bpm", id: "≥ 100 bpm" },
    ],
  },
  {
    key: "grimace",
    en: "Grimace",
    id: "Grimace",
    options: [
      { score: 0, en: "No response", id: "Tidak respons" },
      { score: 1, en: "Grimace / weak cry", id: "Meringis / lemah" },
      { score: 2, en: "Cough · sneeze · cry", id: "Batuk · bersin · nangis" },
    ],
  },
  {
    key: "activity",
    en: "Activity",
    id: "Activity",
    options: [
      { score: 0, en: "Limp / flaccid", id: "Lemas / flasid" },
      { score: 1, en: "Some flexion", id: "Sedikit fleksi" },
      { score: 2, en: "Active motion", id: "Gerakan aktif" },
    ],
  },
  {
    key: "respiration",
    en: "Respiration",
    id: "Respiration",
    options: [
      { score: 0, en: "Absent", id: "Tidak ada" },
      { score: 1, en: "Slow · weak cry", id: "Lambat · lemah" },
      { score: 2, en: "Good strong cry", id: "Tangisan kuat" },
    ],
  },
];

const INTERVALS = [
  { key: "m1", en: "Minute 1", id: "Menit 1" },
  { key: "m5", en: "Minute 5", id: "Menit 5" },
  { key: "m10", en: "Minute 10", id: "Menit 10" },
];

const SCORE_STYLE = {
  0: {
    idle: "border-red-200 bg-red-50/60 text-red-700 hover:bg-red-100",
    active: "border-red-600 bg-red-200 text-red-900 shadow-inner",
    chip: "bg-red-500 border-red-700 text-white",
  },
  1: {
    idle: "border-amber-200 bg-amber-50/60 text-amber-800 hover:bg-amber-100",
    active: "border-amber-500 bg-amber-100 text-amber-900 shadow-inner",
    chip: "bg-amber-400 border-amber-600 text-amber-900",
  },
  2: {
    idle: "border-teal-200 bg-teal-50/60 text-teal-800 hover:bg-teal-100",
    active: "border-teal-600 bg-teal-100 text-teal-900 shadow-inner",
    chip: "bg-teal-600 border-teal-700 text-white",
  },
};

function totalStyle(total, filled) {
  if (!filled) return "text-stone-400 bg-stone-100 border-stone-200";
  if (total >= 7) return "text-teal-800 bg-teal-100 border-teal-500";
  if (total >= 4) return "text-amber-900 bg-amber-100 border-amber-500";
  return "text-red-900 bg-red-100 border-red-500";
}

export default function Apgar() {
  const { lang } = useLanguage();
  const isID = lang === "id";
  const [copied, setCopied] = useState(false);

  // scores[intervalKey][criterionKey] = 0 | 1 | 2 | null
  const [scores, setScores] = useState(() => {
    const seed = {};
    INTERVALS.forEach((iv) => {
      seed[iv.key] = { appearance: null, pulse: null, grimace: null, activity: null, respiration: null };
    });
    return seed;
  });

  const setCell = (ivKey, critKey, score) =>
    setScores((old) => ({ ...old, [ivKey]: { ...old[ivKey], [critKey]: score } }));

  const totals = useMemo(() => {
    const out = {};
    INTERVALS.forEach((iv) => {
      const vals = Object.values(scores[iv.key]);
      const filled = vals.every((v) => v !== null);
      const sum = vals.reduce((a, b) => a + (b ?? 0), 0);
      out[iv.key] = { sum, filled };
    });
    return out;
  }, [scores]);

  const reportStr = INTERVALS.map((iv) => (totals[iv.key].filled ? totals[iv.key].sum : "–")).join(" / ");
  const anyFilled = INTERVALS.some((iv) => totals[iv.key].filled);

  const handleCopy = () => {
    navigator.clipboard?.writeText(`Apgar: ${reportStr}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="apgar-page">
      <header className="border-b border-stone-200 pb-5 mb-8">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-teal-700 mb-2 flex items-center gap-2">
          <Baby size={14} strokeWidth={1.5} />
          {isID ? "Neonatal · Penilaian Bayi Baru Lahir" : "Neonatal · Newborn Assessment"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {isID ? "Skor Apgar" : "Apgar Score"}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "Isi skor pada tiap menit (1, 5, 10). Setiap kolom dihitung independen — hasil akhir dilaporkan sebagai rangkaian angka menit-1 / menit-5 / menit-10."
            : "Score each criterion at 1, 5 and 10 minutes. Each column is scored independently — the final report is a string minute-1 / minute-5 / minute-10."}
        </p>
      </header>

      {/* Matrix */}
      <div
        className="border border-stone-200 bg-white rounded-md overflow-x-auto"
        data-testid="apgar-matrix"
      >
        <div className="min-w-[720px]">
          {/* Header row */}
          <div className="grid grid-cols-[140px_repeat(3,1fr)] border-b border-stone-200 bg-stone-50">
            <div className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-stone-500 border-r border-stone-200">
              {isID ? "Kriteria" : "Criterion"}
            </div>
            {INTERVALS.map((iv, i) => (
              <div
                key={iv.key}
                className={cn(
                  "px-4 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-stone-700 text-center",
                  i < INTERVALS.length - 1 && "border-r border-stone-200"
                )}
              >
                {isID ? iv.id : iv.en}
              </div>
            ))}
          </div>

          {/* Body rows */}
          {CRITERIA.map((c, ci) => (
            <div
              key={c.key}
              className="grid grid-cols-[140px_repeat(3,1fr)] border-b border-stone-200 last:border-b-0"
            >
              <div className="px-4 py-3 font-display text-sm font-medium text-stone-900 border-r border-stone-200 flex items-center bg-stone-50/50">
                {isID ? c.id : c.en}
              </div>
              {INTERVALS.map((iv, ii) => {
                const val = scores[iv.key][c.key];
                return (
                  <div
                    key={iv.key}
                    className={cn(
                      "p-2 space-y-1.5",
                      ii < INTERVALS.length - 1 && "border-r border-stone-200"
                    )}
                    data-testid={`cell-${iv.key}-${c.key}`}
                  >
                    {c.options.map((opt) => {
                      const active = val === opt.score;
                      const style = SCORE_STYLE[opt.score];
                      const label = isID ? opt.id : opt.en;
                      return (
                        <button
                          key={opt.score}
                          type="button"
                          aria-pressed={active}
                          data-testid={`apgar-${iv.key}-${c.key}-${opt.score}`}
                          onClick={() => setCell(iv.key, c.key, opt.score)}
                          className={cn(
                            "w-full border-2 rounded-md px-2 py-1.5 text-left text-[11px] font-medium transition-colors flex items-center gap-2 min-h-[38px]",
                            active ? style.active : style.idle
                          )}
                        >
                          <span className={cn("inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-mono font-bold border shrink-0", style.chip)}>
                            {opt.score}
                          </span>
                          <span className="leading-tight">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Totals row */}
          <div className="grid grid-cols-[140px_repeat(3,1fr)] bg-stone-50">
            <div className="px-4 py-4 font-display text-sm font-semibold text-stone-800 border-r border-stone-200 border-t border-stone-300 flex items-center">
              {isID ? "Total" : "Total"}
            </div>
            {INTERVALS.map((iv, i) => {
              const { sum, filled } = totals[iv.key];
              const style = totalStyle(sum, filled);
              return (
                <div
                  key={iv.key}
                  className={cn(
                    "px-4 py-4 border-t border-stone-300 flex items-center justify-center gap-2",
                    i < INTERVALS.length - 1 && "border-r border-stone-200"
                  )}
                  data-testid={`apgar-total-${iv.key}`}
                >
                  <span className={cn("inline-flex items-center justify-center h-10 w-10 rounded-full border-2 font-display text-lg font-semibold", style)}>
                    {filled ? sum : "–"}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">/ 10</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Combined report */}
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="apgar-report">
        <div className="flex-1 border-2 border-stone-200 bg-white rounded-md px-5 py-4">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-stone-500 mb-1">
            {isID ? "Laporan (min-1 / min-5 / min-10)" : "Report (min-1 / min-5 / min-10)"}
          </div>
          <div className="flex items-baseline gap-3">
            {INTERVALS.map((iv, i) => {
              const { sum, filled } = totals[iv.key];
              const style = totalStyle(sum, filled);
              return (
                <React.Fragment key={iv.key}>
                  <span className={cn("font-display text-3xl md:text-4xl font-semibold px-3 py-0.5 rounded-md border-2", style)} data-testid={`report-num-${iv.key}`}>
                    {filled ? sum : "–"}
                  </span>
                  {i < INTERVALS.length - 1 && <span className="text-stone-300 text-3xl font-light">/</span>}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          data-testid="apgar-copy"
          disabled={!anyFilled}
          onClick={handleCopy}
          className={cn(
            "inline-flex items-center justify-center gap-2 border-2 rounded-md px-5 py-3 min-h-[52px] text-sm font-medium transition-colors",
            !anyFilled
              ? "border-stone-200 text-stone-400 cursor-not-allowed"
              : copied
              ? "border-teal-600 bg-teal-600 text-white"
              : "border-sky-700 text-sky-700 hover:bg-sky-700 hover:text-white"
          )}
        >
          {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
          {copied ? (isID ? "Tersalin!" : "Copied!") : isID ? "Salin ke rekam medis" : "Copy to record"}
        </button>
      </div>

      <div className="mt-8 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">{isID ? "Sebagai rujukan saja." : "Reference only."}</span>{" "}
          {isID
            ? "Skor Apgar tidak boleh menunda resusitasi. Bila bayi apnu / megap-megap atau HR < 100 langsung mulai VTP — skor dinilai retrospektif untuk dokumentasi."
            : "The Apgar score must not delay resuscitation. If the baby is apnoeic / gasping or HR < 100, start PPV immediately — the score is recorded retrospectively for documentation."}
        </div>
      </div>
    </div>
  );
}
